/** Parametric engine for the /vision hero. Every position is a pure function
    of (now, t, f): t = company growth 0..1, f = alignment flip 0..1. No force
    simulation, no React. Drawing matches DecisionNetwork's hand-rolled canvas
    pattern. */

import { ELEVATION_BY_SCOPE, FACT_SCOPE } from "@/app/lib/constants/elevations";

interface Person {
  phase: number;
  speed: number;
}

interface Decision {
  fairOwner: number;
  powerOwner: number;
  pooled: boolean;
  /** Pooled decisions still visibly arriving: they spiral from their
      maker's orbit into the leader's queue on a repeating cycle */
  transit: boolean;
  transitSpeed: number;
  gray: boolean;
  spill: boolean;
  vagueAngle: number;
  vagueRf: number;
  orbitA: number;
  orbitR: number;
  orbitSpeed: number;
  phase: number;
  elev: number;
}

interface FieldParticle {
  /** Normalized spawn position, 0..1 of canvas extent */
  x0: number;
  y0: number;
  /** Parallax factor: deeper particles drift slower and fainter */
  depth: number;
}

export interface GravityFieldState {
  people: Person[];
  decisions: Decision[];
  /** Weather-map streaklines: the world the company moves through */
  field: FieldParticle[];
  /** Background field scroll offset, accumulated across frames */
  bgOffX: number;
  bgOffY: number;
}

export interface FrameParams {
  /** CSS-pixel canvas size (context is already DPR-scaled) */
  width: number;
  height: number;
  /** Timestamp driving ambient motion; freeze it for reduced-motion renders */
  now: number;
  /** Milliseconds since last frame, for background scroll accumulation */
  dt: number;
  /** Company growth 0..1 */
  t: number;
  /** Alignment flip 0..1 */
  f: number;
  /** Sparse array by person index: people with an entry render the photo
      clipped inside their circle, full color in both worlds. */
  avatars?: (HTMLImageElement | undefined)[];
}

/** Enough people to read as a real company while still fitting the fixed
    rows without crowding */
const MAX_PEOPLE = 46;
const MAX_DECISIONS = 340;
const POWER_PLAYERS = 3;

/** People sit in five fixed rows, identical in both worlds. The figure's
    argument is that the org chart was never the problem: people stay in
    their seats while the decision layer reorganizes around them. */
const PEOPLE_ROW_KS = [-2, -1, 0, 1, 2] as const;
/** Row pitch as a fraction of the company radius */
const PEOPLE_ROW_PITCH_F = 0.371;
/** People rows inscribe inside this fraction of the radius so they clear
    the boundary even at its deepest inward breath */
const PEOPLE_DISC_F = 0.8;
const PERSON_RADIUS = 6.5;
/** Avatar circles run larger so the face is legible at a glance */
const AVATAR_RADIUS = 11;
/** Horizontal room a person occupies within a row */
const PERSON_SLOT_W = 17;
const AVATAR_SLOT_W = 26;
/** Tiny breathing so the fixed rows still read as alive */
const PERSON_BREATH = 1.5;
/** Power players anchor the middle rows at set fractions: spread apart the
    way leadership sits inside a real company, not clustered at the top */
const POWER_PLACEMENT = [
  { row: 1, frac: 0.5 },
  { row: 2, frac: 0.28 },
  { row: 3, frac: 0.72 },
] as const;
/** Non-power avatar carriers anchor the outer rows so faces spread across
    the whole figure instead of stacking wherever the greedy fill lands */
const EXTRA_AVATAR_SPOTS = [
  { row: 0, frac: 0.38 },
  { row: 4, frac: 0.66 },
  { row: 0, frac: 0.7 },
  { row: 4, frac: 0.3 },
] as const;
/** Travel direction: due right, so the field streams horizontally past */
const HEADING = 0;
/** Background scroll in px/ms at 1x cruise */
const BASE_VELOCITY = 0.022;
const FIELD_PARTICLES = 130;
/** Streak length in px at 1x cruise; stretches with the velocity multiplier */
const STREAK_LENGTH = 9;
/** Flip multiplies cruise speed up to this: the aligned company moves
    visibly, unmistakably faster through the world. */
export const MAX_VELOCITY_MULT = 4.5;

const ELEV_RGB = [
  ELEVATION_BY_SCOPE[FACT_SCOPE.STRATEGIC].rgb,
  ELEVATION_BY_SCOPE[FACT_SCOPE.STRUCTURAL].rgb,
  ELEVATION_BY_SCOPE[FACT_SCOPE.OPERATIONAL].rgb,
];
const INK_RGB = "73, 80, 87"; // gray.7
const EDGE_RGB = "173, 181, 189"; // gray.5
const SHADOW_RGB = "206, 212, 218"; // gray.4: shadow decisions, unreachable
const FIELD_RGB = "148, 163, 184";
const FABRIC_RGB = "116, 192, 252"; // blue.3: aligned-world links
/** The boundary stays gray in both worlds: the color story belongs to the
    decision nodes, not the company outline */
const BOUNDARY_RESTING = "#ced4da"; // gray.4

function parseRgb(rgb: string): [number, number, number] {
  const [r, g, b] = rgb.split(",").map(Number);
  return [r, g, b];
}

const ELEV_TUPLES = ELEV_RGB.map(parseRgb);
const SHADOW_TUPLE = parseRgb(SHADOW_RGB);

/** Deterministic LCG so the field looks identical on every visit */
function makeRng(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

export function createGravityFieldState(): GravityFieldState {
  const rnd = makeRng(7);

  const people: Person[] = [];
  for (let i = 0; i < MAX_PEOPLE; i++) {
    people.push({
      phase: rnd() * Math.PI * 2,
      speed: 0.3 + rnd() * 0.4,
    });
  }

  const decisions: Decision[] = [];
  for (let j = 0; j < MAX_DECISIONS; j++) {
    const roll = rnd();
    decisions.push({
      fairOwner: j % MAX_PEOPLE,
      powerOwner: Math.floor(rnd() * POWER_PLAYERS),
      pooled: roll < 0.52,
      transit: rnd() < 0.18 && roll < 0.52,
      transitSpeed: 0.7 + rnd() * 0.6,
      gray: roll >= 0.52 && roll < 0.82,
      spill: roll >= 0.92,
      vagueAngle: rnd() * Math.PI * 2,
      vagueRf: 0.3 + rnd() * 0.75,
      orbitA: rnd() * Math.PI * 2,
      orbitR: 12 + rnd() * 14,
      orbitSpeed: (rnd() < 0.5 ? -1 : 1) * (0.15 + rnd() * 0.25),
      phase: rnd() * Math.PI * 2,
      elev: Math.floor(rnd() * 3),
    });
  }

  const field: FieldParticle[] = [];
  for (let m = 0; m < FIELD_PARTICLES; m++) {
    field.push({
      x0: rnd(),
      y0: rnd(),
      depth: 0.45 + rnd() * 0.55,
    });
  }

  return { people, decisions, field, bgOffX: 0, bgOffY: 0 };
}

function lerp(a: number, b: number, k: number): number {
  return a + (b - a) * k;
}

/** Where a transit decision is in its fall, 0 at the maker, 1 at the
    leader's queue. The cycle repeats: the backlog never stops arriving. */
function transitCycle(d: Decision, now: number): number {
  return (now * 0.00008 * d.transitSpeed + d.phase) % 1;
}

/** Counts ramp with growth, gently superlinear so the end state feels most
    crowded but the middle stages already read as busy companies. Narrow
    canvases get a thinner population so dots stay legible. */
function peopleCount(t: number, scale: number): number {
  const max = Math.round(MAX_PEOPLE * scale);
  return Math.max(4, Math.round(4 + t ** 1.35 * (max - 4)));
}

function decisionCount(t: number, scale: number): number {
  const max = Math.round(MAX_DECISIONS * scale);
  return Math.max(7, Math.round(7 + t ** 1.35 * (max - 7)));
}

/** Shape noise. A young company is a near-perfect circle; growth makes the
    silhouette increasingly amorphous (low harmonics lopside it, high
    harmonics roughen it). The flip settles the mess most of the way back,
    but never to a perfect circle: alignment organizes a company, it does
    not make it young again. Amplitude stays modest so the boundary's
    deepest inward breath still clears the fixed people rows. */
function blobFactor(a: number, now: number, t: number, f: number): number {
  const mess = lerp(0.08, 0.6, t ** 1.3) * (1 - f * 0.75);
  return (
    1 +
    (0.065 * Math.sin(2 * a - now * 0.00007 + 2.9) +
      0.058 * Math.sin(3 * a + now * 0.00012) +
      0.042 * Math.sin(5 * a - now * 0.00009 + 1.7) +
      0.026 * Math.sin(8 * a + now * 0.00015 + 4.1)) *
      mess
  );
}

function boundaryR(a: number, now: number, rr: number, t: number, f: number): number {
  return rr * blobFactor(a, now, t, f);
}

interface Point {
  x: number;
  y: number;
}

/** Sweeps stray dots inside the boundary during the flip */
function clampToBoundary(
  pos: Point,
  cx: number,
  cy: number,
  now: number,
  rr: number,
  t: number,
  f: number,
): Point {
  if (f < 0.3) return pos;
  const dx = pos.x - cx;
  const dy = pos.y - cy;
  const d = Math.sqrt(dx * dx + dy * dy);
  if (d < 1) return pos;
  const maxR = boundaryR(Math.atan2(dy, dx), now, rr, t, f) * 0.93;
  if (d <= maxR) return pos;
  return { x: cx + (dx / d) * maxR, y: cy + (dy / d) * maxR };
}

function halfWidthAt(dy: number, rr: number): number {
  const k = 1 - (dy * dy) / (rr * rr);
  return k > 0 ? Math.sqrt(k) * rr : 0;
}

/** Honeycomb spacing: dots sit HEX_SPACING apart in a row, alternate rows
    shift half a step and nest at the hexagonal row height */
const HEX_SPACING = 10;

/** Vertical clearance between a people row's centerline and the nearest
    dot row, sized to the avatar circles */
const SHELF_CLEARANCE = AVATAR_RADIUS + 2.6 + 2;
/** Dot rows inscribe inside this fraction of the radius, just outside the
    people disc, so the lattice also clears the breathing boundary */
const LATTICE_DISC_F = 0.85;

/** Fixed people rows, identical in both worlds. Each row's width budget
    follows the disc; people pack with wider slots for avatar carriers, and
    a full row compresses slightly rather than overflowing. */
function computePeopleSlots(
  np: number,
  cx: number,
  cy: number,
  rr: number,
  avatars?: (HTMLImageElement | undefined)[],
): Point[] {
  const dys = PEOPLE_ROW_KS.map((k) => k * PEOPLE_ROW_PITCH_F * rr);
  const budgets = dys.map((dy) => 2 * Math.sqrt(Math.max(0, (PEOPLE_DISC_F * rr) ** 2 - dy * dy)));
  const remaining = [...budgets];
  const rows: number[][] = dys.map(() => []);
  const widthOf = (i: number): number => (avatars?.[i] ? AVATAR_SLOT_W : PERSON_SLOT_W);

  const anchors: { person: number; row: number; frac: number }[] = [];
  POWER_PLACEMENT.forEach((spot, i) => {
    if (i < np) anchors.push({ person: i, row: spot.row, frac: spot.frac });
  });

  let extra = 0;
  if (avatars) {
    for (let i = POWER_PLAYERS; i < np && extra < EXTRA_AVATAR_SPOTS.length; i++) {
      if (!avatars[i]) continue;
      anchors.push({ person: i, ...EXTRA_AVATAR_SPOTS[extra] });
      extra++;
    }
  }

  const placed = new Set<number>();
  for (const anchor of anchors) {
    rows[anchor.row].push(anchor.person);
    remaining[anchor.row] -= widthOf(anchor.person);
    placed.add(anchor.person);
  }

  // Everyone else flows to whichever row has the most room left, which
  // keeps the rows filling evenly from the widest outward
  for (let i = 0; i < np; i++) {
    if (placed.has(i)) continue;

    let best = 0;
    for (let k = 1; k < rows.length; k++) {
      if (remaining[k] > remaining[best]) best = k;
    }

    rows[best].push(i);
    remaining[best] -= widthOf(i);
  }

  // Slide each anchored person to their fraction within the row
  for (const anchor of anchors) {
    const row = rows[anchor.row];
    row.splice(row.indexOf(anchor.person), 1);
    row.splice(Math.round(anchor.frac * row.length), 0, anchor.person);
  }

  const slots: Point[] = new Array(np);
  rows.forEach((members, k) => {
    const total = members.reduce((sum, m) => sum + widthOf(m), 0);
    if (total === 0) return;

    const scale = Math.min(1, budgets[k] / total);
    let cursor = (-total * scale) / 2;

    for (const m of members) {
      const w = widthOf(m) * scale;
      slots[m] = { x: cx + cursor + w / 2, y: cy + dys[k] };
      cursor += w;
    }
  });

  return slots;
}

/** Lattice slots in the aligned world, one per decision, filled top to
    bottom in elevation order (strategy, structure, operations) so the
    record reads as three stacked bands. Dots live on the shelves between
    the fixed people rows; spacing tightens until the population fits.
    Callers pass the non-breathing radius so the grid never shimmers. */
function computeLatticeSlots(
  state: GravityFieldState,
  cx: number,
  cy: number,
  rr: number,
  nd: number,
): Point[] {
  const dys = PEOPLE_ROW_KS.map((k) => k * PEOPLE_ROW_PITCH_F * rr);

  let spacing = HEX_SPACING;
  let rows: { dy: number; capacity: number }[] = [];
  for (;;) {
    rows = [];
    let total = 0;

    for (let k = 0; k + 1 < dys.length; k++) {
      const top = dys[k] + SHELF_CLEARANCE;
      const bottom = dys[k + 1] - SHELF_CLEARANCE;
      const pitch = spacing * 0.866;
      const count = Math.max(1, Math.floor((bottom - top) / pitch) + 1);
      const first = (top + bottom) / 2 - ((count - 1) * pitch) / 2;

      for (let r = 0; r < count; r++) {
        const dy = first + r * pitch;
        const hw = Math.min(
          halfWidthAt(dy, rr) * 0.82,
          Math.sqrt(Math.max(0, (LATTICE_DISC_F * rr) ** 2 - dy * dy)),
        );
        const capacity = Math.max(0, Math.floor((2 * hw) / spacing) + 1);
        rows.push({ dy, capacity });
        total += capacity;
      }
    }

    if (total >= nd || spacing <= 6) break;
    spacing -= 0.5;
  }

  const order: number[] = [];
  for (let elev = 0; elev < ELEV_RGB.length; elev++) {
    for (let j = 0; j < nd; j++) {
      if (state.decisions[j].elev === elev) order.push(j);
    }
  }

  const slots: Point[] = new Array(nd);
  let m = 0;
  rows.forEach((row, r) => {
    if (m >= nd) return;

    const inRow = Math.min(row.capacity, nd - m);
    const xStart = cx - ((inRow - 1) * spacing) / 2 + (r % 2 === 1 ? spacing / 2 : 0);

    for (let c = 0; c < inRow; c++) {
      slots[order[m]] = { x: xStart + c * spacing, y: cy + row.dy };
      m++;
    }
  });

  // Safety: anything past capacity stacks on the last shelf's centerline
  while (m < nd) {
    slots[order[m]] = { x: cx, y: cy + rows[rows.length - 1].dy };
    m++;
  }

  return slots;
}

/** People never move between worlds: the same seat under gravity and on
    the record, with only a breath of motion. The transformation belongs
    entirely to the decision layer. */
function personPos(state: GravityFieldState, i: number, slots: Point[], now: number): Point {
  const p = state.people[i];
  return {
    x: slots[i].x + Math.sin(now * 0.0004 * p.speed + p.phase) * PERSON_BREATH,
    y: slots[i].y + Math.cos(now * 0.00035 * p.speed + p.phase) * PERSON_BREATH,
  };
}

function decisionPos(
  state: GravityFieldState,
  j: number,
  ppos: Point[],
  lattice: Point[],
  cx: number,
  cy: number,
  rr: number,
  now: number,
  t: number,
  f: number,
): Point {
  const d = state.decisions[j];
  const orbitA = d.orbitA + now * 0.0003 * d.orbitSpeed;

  // Shadow and spill decisions lag the flip: the record visibly absorbs the
  // off-record work instead of erasing it the instant the switch turns on
  const fd = d.gray || d.spill ? Math.max(0, Math.min(1, (f - 0.25) / 0.75)) : f;

  let gx: number;
  let gy: number;

  if (t < 0.18 || (!d.pooled && !d.gray && !d.spill)) {
    // Healthy: orbits its actual maker
    const o = ppos[d.fairOwner % ppos.length];
    gx = o.x + Math.cos(orbitA) * d.orbitR;
    gy = o.y + Math.sin(orbitA) * d.orbitR;
  } else if (d.pooled) {
    // Pooled: queues around one of the power players as the company grows.
    // Clearance keeps the ring outside the leader's circle so the crowding
    // reads as a backlog around a person, not visual noise on top of them
    const c = ppos[d.powerOwner];
    const pull = Math.min(1, (t - 0.15) * 1.8);
    const o2 = ppos[d.fairOwner % ppos.length];
    const ringR = 12 + d.orbitR * 0.8;

    if (d.transit && pull >= 1) {
      // In-fall: a slice of the backlog is always arriving. The dot leaves
      // its maker's orbit and spirals into the leader's queue, faster the
      // closer it gets, then the cycle restarts at the maker. The motion is
      // the argument: decisions fall toward the same few people.
      const fall = transitCycle(d, now) ** 2;
      const ox = o2.x + Math.cos(orbitA) * d.orbitR;
      const oy = o2.y + Math.sin(orbitA) * d.orbitR;
      const r0 = Math.max(ringR, Math.hypot(ox - c.x, oy - c.y));
      const a0 = Math.atan2(oy - c.y, ox - c.x);
      const rNow = lerp(r0, ringR, fall);
      const aNow = a0 + fall * 0.9;
      gx = c.x + Math.cos(aNow) * rNow;
      gy = c.y + Math.sin(aNow) * rNow;
    } else {
      gx = lerp(o2.x + Math.cos(orbitA) * d.orbitR, c.x + Math.cos(orbitA) * ringR, pull);
      gy = lerp(o2.y + Math.sin(orbitA) * d.orbitR, c.y + Math.sin(orbitA) * ringR, pull);
    }
  } else {
    // Shadow or spill: floats in the vacuum, connected to nothing
    const rf = d.vagueRf + (d.spill ? Math.min(0.5, t * 0.55) : 0);
    gx =
      cx +
      Math.cos(d.vagueAngle) * rf * rr * blobFactor(d.vagueAngle, now, t, fd) +
      Math.sin(now * 0.0002 + d.phase) * 6;
    gy =
      cy +
      Math.sin(d.vagueAngle) * rf * rr * blobFactor(d.vagueAngle, now, t, fd) +
      Math.cos(now * 0.00018 + d.phase) * 6;
  }

  const lp = lattice[j];
  return clampToBoundary({ x: lerp(gx, lp.x, fd), y: lerp(gy, lp.y, fd) }, cx, cy, now, rr, t, fd);
}

/** Renders one frame. Mutates only state.bgOffX/bgOffY (field scroll). */
export function drawFrame(
  ctx: CanvasRenderingContext2D,
  state: GravityFieldState,
  params: FrameParams,
): void {
  const { width, height, now, dt, t, f } = params;
  const cx = width / 2;
  const cy = height / 2;

  ctx.clearRect(0, 0, width, height);

  // Background field: the world going past. Always cruising at 1x; the flip
  // takes it to MAX_VELOCITY_MULT.
  const mult = 1 + f * (MAX_VELOCITY_MULT - 1);
  const vel = BASE_VELOCITY * mult;
  state.bgOffX -= Math.cos(HEADING) * vel * dt;
  state.bgOffY -= Math.sin(HEADING) * vel * dt;

  // Streaklines, the weather-map grammar for a moving field: each particle
  // drifts past with parallax depth, and streaks stretch with velocity so
  // the flip reads as wind picking up rather than dots scrolling faster
  const span = width + STREAK_LENGTH * 4;
  const len = STREAK_LENGTH * mult;
  ctx.lineCap = "round";
  ctx.lineWidth = 1.1;
  for (const particle of state.field) {
    const drift = state.bgOffX * particle.depth;
    const px = (((particle.x0 * span + drift) % span) + span) % span;
    const py = particle.y0 * height;
    ctx.beginPath();
    ctx.moveTo(px - STREAK_LENGTH * 2, py);
    ctx.lineTo(px - STREAK_LENGTH * 2 + len * particle.depth, py);
    ctx.strokeStyle = `rgba(${FIELD_RGB}, ${0.2 + 0.25 * particle.depth})`;
    ctx.stroke();
  }

  // The company grows with headcount: a small founding circle expanding to
  // fill most of the canvas at scale. The boundary breathes slightly; the
  // breath-free radius anchors the static lattice.
  const rrLattice = (Math.min(width, height) / 2) * 0.78 * lerp(0.35, 1, t);
  const rr = rrLattice + Math.sin(now * 0.0006) * 2;
  const popScale = width < 520 ? 0.7 : 1;
  const np = peopleCount(t, popScale);
  const nd = decisionCount(t, popScale);

  // Boundary
  ctx.beginPath();
  const STEPS = 280;
  for (let s = 0; s <= STEPS; s++) {
    const a = (s / STEPS) * Math.PI * 2;
    const r = boundaryR(a, now, rr, t, f);
    if (s === 0) ctx.moveTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
    else ctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
  }
  ctx.closePath();
  // Opaque company: the field streams past behind it, never through it
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.strokeStyle = BOUNDARY_RESTING;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  const pslots = computePeopleSlots(np, cx, cy, rrLattice, params.avatars);
  const ppos: Point[] = [];
  for (let i = 0; i < np; i++) ppos.push(personPos(state, i, pslots, now));

  const lattice = computeLatticeSlots(state, cx, cy, rrLattice, nd);
  const dpos: Point[] = [];
  for (let j = 0; j < nd; j++) {
    dpos.push(decisionPos(state, j, ppos, lattice, cx, cy, rr, now, t, f));
  }

  // Gravity-world ownership links: decision -> whoever holds it
  if (f < 0.95) {
    for (let j = 0; j < nd; j++) {
      const d = state.decisions[j];
      if ((d.gray || d.spill) && t > 0.18) continue;
      const owner = t < 0.18 || !d.pooled ? ppos[d.fairOwner % ppos.length] : ppos[d.powerOwner];
      ctx.beginPath();
      ctx.moveTo(dpos[j].x, dpos[j].y);
      ctx.lineTo(owner.x, owner.y);
      ctx.strokeStyle = `rgba(${EDGE_RGB}, ${0.2 * (1 - f)})`;
      ctx.lineWidth = 0.75;
      ctx.stroke();
    }
  }

  // Aligned-world fabric: nearest neighbor within each elevation stratum
  if (f > 0.05) {
    for (let j = 0; j < nd; j++) {
      let best = -1;
      let bd = Infinity;
      for (let k = 0; k < nd; k++) {
        if (k === j || state.decisions[k].elev !== state.decisions[j].elev) continue;
        const dx = dpos[j].x - dpos[k].x;
        const dy = dpos[j].y - dpos[k].y;
        const dist = dx * dx + dy * dy;
        if (dist < bd) {
          bd = dist;
          best = k;
        }
      }
      if (best > j) {
        ctx.beginPath();
        ctx.moveTo(dpos[j].x, dpos[j].y);
        ctx.lineTo(dpos[best].x, dpos[best].y);
        ctx.strokeStyle = `rgba(${FABRIC_RGB}, ${0.22 * f})`;
        ctx.lineWidth = 0.6;
        ctx.stroke();
      }
    }
  }

  // Decisions first, then people on top: opaque people occlude the dots
  for (let j = 0; j < nd; j++) {
    const d = state.decisions[j];
    const isShadow = (d.gray || d.spill) && t > 0.18;

    // Transit dots fade in at the maker so each cycle reads as a fresh
    // decision starting its fall, not the same dot snapping back out
    let alpha = 1;
    if (d.transit && d.pooled && Math.min(1, (t - 0.15) * 1.8) >= 1 && f < 0.5) {
      const fadeIn = Math.min(1, transitCycle(d, now) / 0.12);
      alpha = lerp(fadeIn, 1, f * 2);
    }

    ctx.beginPath();
    ctx.arc(dpos[j].x, dpos[j].y, 2.6, 0, Math.PI * 2);

    if (isShadow) {
      // Surfacing: tint from shadow gray to the elevation color as the flip
      // progresses, so off-record decisions visibly join the record
      const surface = Math.max(0, Math.min(1, (f - 0.35) / 0.55));
      const c = ELEV_TUPLES[d.elev];
      const r = Math.round(lerp(SHADOW_TUPLE[0], c[0], surface));
      const g = Math.round(lerp(SHADOW_TUPLE[1], c[1], surface));
      const b = Math.round(lerp(SHADOW_TUPLE[2], c[2], surface));
      ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
    } else {
      ctx.fillStyle = f > 0.5 ? `rgb(${ELEV_RGB[d.elev]})` : `rgb(${INK_RGB})`;
    }

    if (alpha < 1) {
      ctx.globalAlpha = alpha;
      ctx.fill();
      ctx.globalAlpha = 1;
    } else {
      ctx.fill();
    }
  }

  for (let i = 0; i < np; i++) {
    // People are people in both worlds: same seat, same size. Power players
    // keep a thicker stroke, a presence rather than a chokepoint.
    const isPower = i < POWER_PLAYERS && t > 0.3;
    const avatar = params.avatars?.[i];
    const hasAvatar = !!avatar && avatar.complete && avatar.naturalWidth > 0;
    const pr = hasAvatar ? AVATAR_RADIUS : PERSON_RADIUS;

    ctx.beginPath();
    ctx.arc(ppos[i].x, ppos[i].y, pr, 0, Math.PI * 2);
    ctx.fillStyle = "#ffffff";
    ctx.fill();

    if (avatar && hasAvatar) {
      // Center-square crop, clipped to the person circle. Full color in
      // both worlds: people are people, only the system around them changes.
      const side = Math.min(avatar.naturalWidth, avatar.naturalHeight);
      ctx.save();
      ctx.clip();
      ctx.drawImage(
        avatar,
        (avatar.naturalWidth - side) / 2,
        (avatar.naturalHeight - side) / 2,
        side,
        side,
        ppos[i].x - pr,
        ppos[i].y - pr,
        pr * 2,
        pr * 2,
      );
      ctx.restore();
    }

    ctx.strokeStyle = `rgb(${INK_RGB})`;
    ctx.lineWidth = isPower ? lerp(2, 1.5, f) : 1.25;
    ctx.stroke();
  }
}
